import { v2, ManifestSection, PageTypeV2, LogSeverity } from '@sap/ux-specification-types';
import { getReflectMetadata } from '../../../common/decoration/decorators';
import { ensureManifestSectionByPathV2 } from '../../export/manifest';
import { MetadataInstanceFactoryV2 as Factory } from '../../export/factory';
import type { MetadataInstanceInterface } from '../../../common/decoration/factory';
import { assignReflectMetadataRules } from '../../export/export';
import { DEFINITION_LINK_PREFIX, getErrorMessage, importProperty } from '../../../common';
import { log, type ImportOverviewPageV2Parameters } from '../../../..';
import type { Definition } from 'typescript-json-schema';
import i18next from 'i18next';

/**
 * Transfers settings of a referenced schema definition to the configuration part.
 *
 * @param {ImportOverviewPageV2Parameters} importParameters - Structure of import parameters.
 * @param {any} definitionArray - Array of definition references from the schema.
 * @param {{ [key: string]: any }} configPart - Current part of the configuration JSON.
 * @param {string} key - Property key of the parent.
 * @param {MetadataInstanceInterface} factory - Metadata factory instance.
 * @param {string[]} pathHierarchy - Parts of the path or breadcrumbs.
 */
function transferSettingsOfRef(
    importParameters: ImportOverviewPageV2Parameters,
    definitionArray: any,
    configPart: { [key: string]: any },
    key: string,
    factory: MetadataInstanceInterface,
    pathHierarchy: string[]
) {
    const targetDefinitionType = definitionArray[definitionArray.length - 1];
    const nextTargetDefinition = (importParameters.jsonSchema as Definition).definitions[targetDefinitionType];
    configPart[key] = assignReflectMetadataRules(factory, key, configPart[key], targetDefinitionType);
    //transfer settings:
    pathHierarchy.push(key);
    processOvpProperties(
        factory,
        configPart[key],
        importParameters,
        (nextTargetDefinition as Definition).properties,
        pathHierarchy
    );
}

/**
 * Resolves the reference of an object-type property and starts the transfer of the properties to the config.
 *
 * @param {{ [key: string]: any }} configPart - current part of the config JSON
 * @param {string} key - property key of parent
 * @param {Definition} schemaProperties - current properties' list of a definition from app schema
 * @param {ImportOverviewPageV2Parameters} importParameters  - structure of import parameters
 * @param {MetadataInstanceInterface} factory - Metadata factory
 * @param {string[]} pathHierarchy - Parts of the path or breadcrumbs
 * @param {v2.CardTemplateType} cardType - Target card type to process
 */
function processPropertiesOfObject(
    configPart: { [key: string]: any },
    key: string,
    schemaProperties: Definition,
    importParameters: ImportOverviewPageV2Parameters,
    factory: MetadataInstanceInterface,
    pathHierarchy: string[],
    cardType?: v2.CardTemplateType
) {
    const isConfigPropertyDefined = key in configPart;
    if (schemaProperties[key].$ref) {
        configPart[key] = configPart[key] ?? {};
        const definitionArray = schemaProperties[key].$ref.split(DEFINITION_LINK_PREFIX);
        transferSettingsOfRef(importParameters, definitionArray, configPart, key, factory, pathHierarchy);
    } else if (key === 'settings' && schemaProperties[key].anyOf && cardType in v2.cardTemplateSettingsMap) {
        configPart[key] = configPart[key] ?? {};
        const ref = `${DEFINITION_LINK_PREFIX}${v2.cardTemplateSettingsMap[cardType]}`;
        const definitionArray = ref.split(DEFINITION_LINK_PREFIX);
        transferSettingsOfRef(importParameters, definitionArray, configPart, key, factory, pathHierarchy);
    }
    if (!isConfigPropertyDefined && !Object.keys(configPart[key]).length) {
        delete configPart[key];
    }
}

/**
 * Imports a single sap.ovp property into the config.
 *
 * @param configPart - part of the config JSON, to be updated
 * @param key - key of the property
 * @param pathHierarchy - Parts of the path or breadcrumbs
 * @param importParameters  - structure of import parameters
 * @param schemaProperties - current properties' list of a definition from app schema
 * @param factory - Metadata factory
 * @param cardType - Target card type to process
 */
function processOvpProperty(
    configPart: { [key: string]: object },
    key: string,
    pathHierarchy: string[],
    importParameters: ImportOverviewPageV2Parameters,
    schemaProperties: Definition,
    factory: MetadataInstanceInterface,
    cardType?: v2.CardTemplateType
): void {
    const syncRule = getReflectMetadata(configPart, key);
    if (syncRule?.manifest) {
        const path = syncRule.manifest.path([...pathHierarchy, key]);
        const manifestSection = ensureManifestSectionByPathV2(importParameters.manifest, path);
        if (Object.prototype.hasOwnProperty.call(manifestSection, key)) {
            try {
                importProperty(syncRule, manifestSection, key, configPart);
            } catch (error) {
                log(importParameters.logger, {
                    severity: LogSeverity.Error,
                    message: i18next.t('XMLPARSEFAILURE', { error: getErrorMessage(error) }),
                    location: {
                        path
                    }
                });
            }
        }
    } else if (key !== 'cards' && key !== '$schema' && typeof schemaProperties[key] === 'object') {
        processPropertiesOfObject(
            configPart,
            key,
            schemaProperties,
            importParameters,
            factory,
            pathHierarchy,
            cardType
        );
    }
}

/**
 * Handles a list of properties of a given part of the schema, transfers the relevant property values or settings from manifest to config.
 *
 * @param {MetadataInstanceInterface} factory - Metadata factory
 * @param {{ [key: string]: any }} configPart - current part of the config JSON
 * @param {ImportOverviewPageV2Parameters} importParameters  - structure of import parameters
 * @param {Definition} schemaProperties - current properties' list of a definition from app schema
 * @param {string[]} pathHierarchy - Parts of the path or breadcrumbs
 * @param {v2.CardTemplateType} cardType - Target card type to process
 */
function processOvpProperties(
    factory: MetadataInstanceInterface,
    configPart: { [key: string]: any },
    importParameters: ImportOverviewPageV2Parameters,
    schemaProperties: Definition,
    pathHierarchy?: string[],
    cardType?: v2.CardTemplateType
): void {
    for (const key in schemaProperties) {
        processOvpProperty(configPart, key, pathHierarchy, importParameters, schemaProperties, factory, cardType);
    }
}

/**
 * OVP: Transfers all settings from manifest to config.
 *
 * @param pageConfig - the OVP configuration (JSON)
 * @param importParameters  - structure of import parameters
 * @param factory - Metadata factory
 */
function addSettings(
    pageConfig: v2.OverviewPageConfigV2,
    importParameters: ImportOverviewPageV2Parameters,
    factory: MetadataInstanceInterface
): void {
    processOvpProperties(
        factory,
        pageConfig,
        importParameters,
        (importParameters.jsonSchema as Definition).properties,
        []
    );

    pageConfig.cards = {};

    Object.keys(importParameters.manifest[ManifestSection.ovp].cards).forEach((cardId) => {
        const cardTemplate = importParameters.manifest[ManifestSection.ovp]['cards'][cardId].template;
        const cardType = v2.cardTemplateTypeMap[cardTemplate] ?? 'CustomCard';

        const cardTypes: Array<{ template: string; cardType: string }> = [];
        for (const template in v2.cardTemplateTypeMap) {
            if (v2.cardTemplateTypeMap[template] !== cardType) {
                cardTypes.push({
                    template,
                    cardType: v2.cardTemplateTypeMap[template]
                });
            }
        }
        // Ensure cardTemplate is moved to the end
        cardTypes.push({
            template: cardTemplate,
            cardType
        });

        for (const variantCard of cardTypes) {
            //instantiate Card:
            pageConfig.cards[cardId] = assignReflectMetadataRules(
                factory,
                variantCard.cardType,
                pageConfig.cards[cardId] ?? {},
                'CustomCard'
            ) as v2.CardType;

            pageConfig.cards = assignReflectMetadataRules(
                factory,
                variantCard.cardType,
                pageConfig.cards,
                'CustomCard'
            ) as {
                [k: string]: v2.CardType;
            };

            //transfer settings:
            processOvpProperties(
                factory,
                pageConfig.cards[cardId],
                importParameters,
                ((importParameters.jsonSchema as Definition).definitions[variantCard.cardType] as Definition)
                    .properties,
                ['cards', cardId],
                variantCard.template as v2.CardTemplateType
            );
        }
    });
}

/**
 * Creates the configuration (file content) for an overview page.
 *
 * @param {ImportOverviewPageV2Parameters} importParameters  - structure of import parameters
 * @returns {object} - the OVP configuration (JSON)
 */
export function createOverviewPageConfig(importParameters: ImportOverviewPageV2Parameters): object {
    const factory = new Factory();
    //Instantiate Page
    const config = factory.createPageInstance(PageTypeV2.OverviewPage);

    if (importParameters.jsonSchema) {
        addSettings(config, importParameters, factory);
    }
    return config;
}
